import {
  getMetadataPreferredHomeRoute,
  resolveHomeRoute,
  type AuthMetadataRole,
  type HomeRoute,
} from "@/lib/auth/role-routing";

export type AuthResolvedRoute = HomeRoute | "/onboarding/role-selection";

type MaybeSingleRow = {
  id?: string | null;
  user_id?: string | null;
};

type MaybeSingleResult = PromiseLike<{
  data: MaybeSingleRow | null;
  error: { message: string } | null;
}>;

export type RouteResolutionClient = {
  from: (table: string) => {
    select: (columns: string) => {
      eq: (column: string, value: string) => {
        maybeSingle: () => MaybeSingleResult;
      };
    };
  };
};

async function hasMembership(
  client: RouteResolutionClient,
  table: "admin_users" | "coaches" | "clients",
  userId: string
): Promise<boolean> {
  const selectColumn = table === "admin_users" ? "user_id" : "id";
  const { data, error } = await client
    .from(table)
    .select(selectColumn)
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("[HomeRouteResolution] Membership lookup failed", {
      table,
      userId,
      error: error.message,
    });
    return false;
  }

  return Boolean(data);
}

export async function resolveHomeRouteForUser(
  client: RouteResolutionClient,
  input: {
    userId: string;
    role: AuthMetadataRole;
  }
): Promise<AuthResolvedRoute> {
  const metadataPreferredRoute = getMetadataPreferredHomeRoute(input.role);

  if (metadataPreferredRoute === "/client") {
    return "/client";
  }

  if (metadataPreferredRoute === "/admin") {
    if (await hasMembership(client, "admin_users", input.userId)) {
      return "/admin";
    }
  }

  if (metadataPreferredRoute === "/coach/dashboard-v2") {
    if (await hasMembership(client, "coaches", input.userId)) {
      return "/coach/dashboard-v2";
    }
  }

  const [hasAdmin, hasCoach, hasClient] = await Promise.all([
    hasMembership(client, "admin_users", input.userId),
    hasMembership(client, "coaches", input.userId),
    hasMembership(client, "clients", input.userId),
  ]);

  if (!metadataPreferredRoute && !hasAdmin && !hasCoach && !hasClient) {
    return "/onboarding/role-selection";
  }

  return resolveHomeRoute({
    role: input.role,
    hasAdmin,
    hasCoach,
    hasClient,
  });
}
